import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../../common/utils';
import {
	type CallerSignals,
	captureCallerSignals,
} from '../../../../common/utils/caller-attribution';
import {
	getItemCategory,
	isProcessingAllowedByPreferencesWithCategory,
} from '../../../../common/utils/item-processing';
import { Logger } from '../../../../common/utils/logger';
import { createIDBOpenDBRequestWrapper } from '../../../../services/indexed-db-wrapper-service/idb-open-request-wrapper';
import { isValidDBName } from '../../../../services/indexed-db-wrapper-service/utils';
import { ItemKeyCache } from '../../../../services/item-key-cache-service';
import { CategoryStorageType, PreferenceCategory } from '../../../../types';

interface DatabaseEventDetails {
	databaseName: string;
}

// DatabaseBlockedError extends DOMException in browser environments, but DOMException is not
// available in SSR/Node.js, which causes import errors. We conditionally extend DOMException
// only when it is available, falling back to a plain Error in SSR — the same pattern used
// for IDBOpenDBRequestWrapper.
let DatabaseBlockedError: new (message?: string, name?: string) => Error;

if (typeof DOMException !== 'undefined') {
	DatabaseBlockedError = class DatabaseBlockedError extends DOMException {};
} else {
	DatabaseBlockedError = class DatabaseBlockedError extends Error {
		constructor(
			message?: string,
			public name: string = 'DatabaseBlockedError',
		) {
			super(message);
		}
	};
}

const checkAndSendAnalyticsEvent = async (eventDetails: DatabaseEventDetails): Promise<void> => {
	const wasSetByPackage = ItemKeyCache.wasSetByPackage(
		CategoryStorageType.IndexedDB,
		eventDetails.databaseName,
	);

	const productName = getBscGlobalState().product;

	const itemCategory = await getItemCategory(
		eventDetails.databaseName,
		CategoryStorageType.IndexedDB,
		productName,
	);

	const isStrictlyNecessary = itemCategory === PreferenceCategory.StrictlyNecessary;

	// Strictly-necessary and package-set databases never block, so omit their telemetry.
	if (!isStrictlyNecessary && !wasSetByPackage) {
		const attributes = {
			...eventDetails,
			productName,
			outsidePackage: true,
			itemCategory: (itemCategory ?? PreferenceCategory.Unknown) as string,
		};

		// Send analytics event for uncategorized IndexedDB usage
		sendPackageOperationalEvent({
			action: 'usedAtlIndexedDBOpen',
			attributes,
		});
	}
};

// Keep forwarding `extensionSignals` to `isProcessingAllowedByPreferencesWithCategory` — extension
// writes rely on it to bypass the consent check.
const checkIfAllowedWithCategory = async (
	dbName: string,
	extensionSignals?: CallerSignals,
): Promise<{ isAllowed: boolean; itemCategory: PreferenceCategory | null | undefined }> => {
	if (!isValidDBName(dbName)) {
		const error = new Error('Invalid database name: must be a non-empty string');
		Logger.errorWithOperationalEvent({
			action: 'usedAtlIndexedDBOpenError',
			attributes: { dbName },
			message: `IndexedDB open failed: ${error.message}`,
		});
		throw error;
	}

	const productName = getBscGlobalState().product;

	const { isAllowed, itemCategory } = await isProcessingAllowedByPreferencesWithCategory({
		itemKey: dbName,
		storageType: CategoryStorageType.IndexedDB,
		productName,
		extensionSignals,
	});
	return { isAllowed, itemCategory };
};

/**
 * @private
 * @deprecated
 */
export const getIndexedDBOpenOverrideEventOnly =
	(originalOpen: (name: string, version?: number) => IDBOpenDBRequest) =>
	(databaseName: string, version?: number): IDBOpenDBRequest => {
		checkAndSendAnalyticsEvent({
			databaseName,
		}).catch((e) => {
			Logger.error(`Failed to send IndexedDB open event. ${e.message || ''}`);
		});

		// Always allow the operation to proceed (we're only tracking usage)
		return originalOpen(databaseName, version);
	};

export const getIndexedDBOpenOverride =
	(originalOpen: (name: string, version?: number) => IDBOpenDBRequest) =>
	(databaseName: string, version?: number): IDBOpenDBRequest => {
		const wasSetByPackage = ItemKeyCache.wasSetByPackage(
			CategoryStorageType.IndexedDB,
			databaseName,
		);
		if (wasSetByPackage) {
			return originalOpen(databaseName, version);
		}

		const extensionSignals = fg('platform_bsc_browser_extension_detection')
			? captureCallerSignals()
			: undefined;

		Logger.debug(`IndexedDB operation: overridden open on database \"${databaseName}\"`, {
			version: String(version ?? 'unspecified'),
		});
		const requestWrapper = createIDBOpenDBRequestWrapper();

		// Always resolve category information for telemetry and so the uncategorized passthrough
		// can distinguish an unknown database from one the user denied.
		const allowUncategorizedOverride =
			!!getBscGlobalState().allowUncategorizedItemsInIndexedDBOverrides;

		const allowedPromise = checkIfAllowedWithCategory(databaseName, extensionSignals).then(
			({ isAllowed, itemCategory }) => ({
				isAllowed,
				itemCategory: itemCategory ?? PreferenceCategory.Unknown,
				categoryAttribute: {
					itemCategory: (itemCategory ?? PreferenceCategory.Unknown) as string,
				},
			}),
		);

		allowedPromise
			.then(({ isAllowed, itemCategory, categoryAttribute }) => {
				// When `allowUncategorizedItemsInIndexedDBOverrides` is enabled, databases that have
				// no known category (null/Unknown) are allowed through instead of being blocked. This
				// is a rollout-safety valve — only uncategorized DBs are affected; DBs that are
				// categorized but user-denied are still blocked regardless of this flag.
				const isUncategorized = itemCategory == null || itemCategory === PreferenceCategory.Unknown;
				const resolvedIsAllowed = isAllowed || (allowUncategorizedOverride && isUncategorized);

				sendPackageOperationalEvent({
					action: 'usedAtlIndexedDBOpen',
					attributes: {
						databaseName,
						wasRejected: !resolvedIsAllowed,
						outsidePackage: true,
						...categoryAttribute,
					},
				});

				if (resolvedIsAllowed) {
					Logger.debug(
						`IndexedDB operation: override on database \"${databaseName}\" was ALLOWED`,
						{ version: String(version ?? 'unspecified') },
					);

					const actualRequest = originalOpen(databaseName, version);

					requestWrapper.wrapRequest(actualRequest);
				} else {
					Logger.debug(
						`IndexedDB operation: override on database \"${databaseName}\" was REJECTED`,
						{ version: String(version ?? 'unspecified') },
					);

					const error = new DatabaseBlockedError(
						'The database was blocked by consent preferences',
						'storageItemBlocked',
					);
					const errorEvent = new ErrorEvent('error', { message: error.message, error });
					requestWrapper.setError(error as unknown as DOMException);
					requestWrapper.dispatchEvent(errorEvent);
				}
			})
			.catch((e) => {
				const errorMessage = e instanceof Error ? e.message : String(e);

				Logger.debug(
					`IndexedDB operation: override on database \"${databaseName}\" ERROR: ${errorMessage}`,
					{ version: String(version ?? 'unspecified') },
				);

				Logger.errorWithOperationalEvent({
					action: 'usedAtlIndexedDBOpenError',
					attributes: {
						databaseName,
						outsidePackage: true,
					},
					message: `IndexedDB open failed during preference check: ${errorMessage}`,
				});
				const errorEvent = new ErrorEvent('error', { message: errorMessage, error: e });
				requestWrapper.dispatchEvent(errorEvent);
			});
		return requestWrapper as IDBOpenDBRequest;
	};
